import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, Tooltip } from "@mimber/ui-web"
import { PushpinOutlined } from "@mimber/ui-web"
import { useAnnouncements } from "@mimber/client/mimber-organization"
import type { ColumnsType } from "@mimber/ui-web"
import type { AnnouncementWithRelationsDto } from "@mimber/schema"
import {
  AnnouncementStatus,
  AnnouncementPriority,
  AudienceTargetType,
} from "@mimber/schema"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  formatTableDate,
} from "@mimber/ui-web"
import { AnnouncementActionsDropdown } from "@/components/dropdowns"
import type { AnnouncementsTableProps } from "./AnnouncementsTable.types"
import { STATUS_CONFIG, PRIORITY_CONFIG } from "./AnnouncementsTable.utils"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const AnnouncementsTable: React.FunctionComponent<
  AnnouncementsTableProps
> = ({ onView, onEdit }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "announcements",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
    })

  const { data, isLoading } = useAnnouncements(queryParams)

  const announcements = data?.data ?? []
  const total = data?.meta?.pagination?.total ?? 0

  const columns: ColumnsType<AnnouncementWithRelationsDto> = useMemo(
    () => [
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filters: Object.values(AnnouncementStatus).map((s) => ({
          text: STATUS_CONFIG[s].label,
          value: s,
        })),
        render: (
          status: AnnouncementStatus,
          record: AnnouncementWithRelationsDto
        ) => (
          <Space size={4}>
            <Tag color={STATUS_CONFIG[status].color}>
              {STATUS_CONFIG[status].label}
            </Tag>
            {record.isPinned && (
              <Tooltip title="Pinned">
                <PushpinOutlined style={{ color: "#faad14" }} />
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "Priority",
        dataIndex: "priority",
        key: "priority",
        filters: Object.values(AnnouncementPriority).map((p) => ({
          text: PRIORITY_CONFIG[p].label,
          value: p,
        })),
        render: (priority: AnnouncementPriority) => (
          <Tag color={PRIORITY_CONFIG[priority].color}>
            {PRIORITY_CONFIG[priority].label}
          </Tag>
        ),
      },
      {
        title: "Target",
        key: "targetType",
        render: (_: unknown, record: AnnouncementWithRelationsDto) => {
          const targetType = record.audienceTarget?.targetType
          if (!targetType || targetType === AudienceTargetType.ALL) {
            return <Tag>Everyone</Tag>
          }
          const groupCount = record.audienceTarget?.groupTargets?.length ?? 0
          const tierCount = record.audienceTarget?.tierTargets?.length ?? 0
          return (
            <Space size={4}>
              {groupCount > 0 && <Tag color="blue">{groupCount} group(s)</Tag>}
              {tierCount > 0 && <Tag color="green">{tierCount} tier(s)</Tag>}
            </Space>
          )
        },
      },
      {
        title: "Author",
        key: "author",
        render: (_: unknown, record: AnnouncementWithRelationsDto) => {
          if (!record.author) return <Text type="secondary">—</Text>
          const { firstName, lastName } = record.author.user
          return (
            <Text>
              {firstName} {lastName}
            </Text>
          )
        },
      },
      {
        title: "Scheduled",
        dataIndex: "scheduledPublishAt",
        key: "scheduledPublishAt",
        sorter: true,
        render: (date: string | null) =>
          date ? formatTableDate(date) : <Text type="secondary">—</Text>,
      },
      {
        title: "Published",
        dataIndex: "publishedAt",
        key: "publishedAt",
        sorter: true,
        render: (date: string | null) =>
          date ? formatTableDate(date) : <Text type="secondary">—</Text>,
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        defaultSortOrder: "descend",
        render: (date: string) => formatTableDate(date),
      },
      {
        title: "Actions",
        key: "actions",
        width: 60,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        }),
        render: (_: unknown, announcement: AnnouncementWithRelationsDto) => (
          <AnnouncementActionsDropdown announcement={announcement} />
        ),
      },
    ],
    [onView, onEdit]
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  return (
    <>
      <Table<AnnouncementWithRelationsDto>
        bordered
        dataSource={announcements}
        columns={displayColumns}
        loading={isLoading}
        rowKey="uuid"
        pagination={getRememberedPaginationConfig(
          total,
          queryParams,
          resetQueryParams
        )}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onRow={(record) => ({
          onClick: () => onView?.(record),
          style: onView ? { cursor: "pointer" } : undefined,
        })}
        onChange={(pagination, filters, sorter) => {
          const tableChangeData = {
            pagination,
            filters: filters as Record<string, string[] | null>,
            sorter: {
              field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
              order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
            } as { field?: string | string[]; order?: "ascend" | "descend" },
          }
          handleTableChange(tableChangeData, setQueryParams, columns)
        }}
      />
    </>
  )
}
